import type { GetBlockedActionsResponseType } from "@app/lib/api/assistant/conversation/blocked_actions";
import { emptyArray, useFetcher, useSWRWithDefaults } from "@app/lib/swr/swr";
import { concurrentExecutor } from "@app/lib/utils/async_utils";

export function useBlockedActions({
  conversationIds,
  workspaceId,
}: {
  conversationIds: string[];
  workspaceId: string;
}) {
  const { fetcher } = useFetcher();
  const disabled = conversationIds.length === 0;

  const { data, error, mutate } = useSWRWithDefaults(
    disabled ? null : ["blockedActions", workspaceId, ...conversationIds],
    async () => {
      const responses: GetBlockedActionsResponseType[] =
        await concurrentExecutor(
          conversationIds,
          (conversationId) =>
            fetcher(
              `/api/w/${workspaceId}/assistant/conversations/${conversationId}/actions/blocked`
            ),
          { concurrency: 4 }
        );
      return responses.flatMap((response) => response.blockedActions);
    },
    // Tracking another conversation changes the key; keep the cards on screen meanwhile.
    { disabled, keepPreviousData: true }
  );

  return {
    // keepPreviousData would otherwise keep serving the last list once disabled.
    blockedActions: disabled
      ? emptyArray<GetBlockedActionsResponseType["blockedActions"][number]>()
      : (data ?? emptyArray()),
    isLoading: !error && !data && !disabled,
    isError: error,
    mutate,
  };
}
